import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Phone, 
  Mail, 
  FileText, 
  CreditCard, 
  Check, 
  X, 
  Eye,
  AlertCircle,
  User,
  Calendar,
  MapPin
} from 'lucide-react';
import apiService from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';

interface PendingUser {
  userId: string;
  profileId: string;
  email: string;
  phone: string;
  name: string;
  age: number;
  dob: string;
  gender: string;
  caste: string;
  subcaste: string;
  gothra: string;
  maritalStatus: string;
  height: string;
  education: string;
  occupation: string;
  annualIncome: string;
  workLocation: {
    type: string;
    state?: string;
    city?: string;
    country?: string;
    address?: string;
  };
  familyLocation: {
    state: string;
    city: string;
  };
  aadhaarNumber: string;
  aadhaarFile?: string;
  panNumber?: string;
  panFile?: string;
  profilePhoto?: string;
  photoCount: number;
  emailVerified: boolean;
  phoneVerified: boolean;
  verificationStatus: string;
  profileStatus: number;
  registeredAt: string;
  verificationScore: number;
  readyForVerification: boolean;
}

const UserVerification: React.FC = () => {
  const [pendingUsers, setPendingUsers] = useState<PendingUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<PendingUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [notes, setNotes] = useState('');
  
  const navigate = useNavigate();

  useEffect(() => {
    loadPendingVerifications();
  }, []);

  const loadPendingVerifications = async () => {
    setLoading(true);
    try {
      const response = await apiService.getPendingVerifications();
      setPendingUsers(response.users || []);
    } catch (error) {
      console.error('Failed to load pending verifications:', error);
      setPendingUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyUser = async (action: 'approve' | 'reject') => {
    if (!selectedUser) return;
    
    setVerifying(true);
    try {
      console.log(`Attempting to ${action} user:`, selectedUser.userId);
      
      const response = await apiService.verifyUser(selectedUser.userId, action, notes);
      
      console.log('Verification response:', response);
      
      if (response.newToken) {
        console.log('New token received, updating localStorage');
        localStorage.setItem('token', response.newToken);
        
        window.dispatchEvent(new CustomEvent('auth-updated', {
          detail: { 
            token: response.newToken,
            user: response.user 
          }
        }));
        
        console.log('Token updated and auth-updated event dispatched');
      }
      
      setPendingUsers(prev => prev.filter(user => user.userId !== selectedUser.userId));
      setSelectedUser(null);
      setNotes('');
      
      const actionText = action === 'approve' ? 'verified' : 'rejected';
      alert(`User ${actionText} successfully${response.newToken ? ' - User token updated' : ''}`);
      
    } catch (error: any) {
      console.error(`Failed to ${action} user:`, error);
      alert(error.message || `Failed to ${action} user`);
    } finally {
      setVerifying(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatText = (text: string) => {
    return text?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '';
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  if (loading) {
    return <LoadingSpinner fullScreen text="Loading pending verifications..." />;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors mr-4"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Dashboard</span>
            </button>
            <h1 className="text-lg font-semibold text-gray-900">User Verifications</h1>
            <div className="ml-auto">
              <span className="text-sm text-gray-600">
                {pendingUsers.length} pending verification{pendingUsers.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {pendingUsers.length === 0 ? (
          <div className="text-center py-12">
            <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Pending Verifications</h3>
            <p className="text-gray-600">All user verifications are up to date!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Users List */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg border border-gray-200">
                <div className="p-4 border-b border-gray-200">
                  <h2 className="font-semibold text-gray-900">Pending Users</h2>
                </div>
                <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
                  {pendingUsers.map(user => (
                    <div
                      key={user.userId}
                      onClick={() => setSelectedUser(user)}
                      className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                        selectedUser?.userId === user.userId ? 'bg-blue-50 border-r-2 border-blue-500' : ''
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                          <span className="text-primary-600 font-medium text-sm">
                            {getInitials(user.name)}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{user.name}</p>
                          <p className="text-sm text-gray-500 truncate">{user.email}</p>
                          <p className="text-xs text-gray-400">{formatDate(user.registeredAt)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* User Details */}
            <div className="lg:col-span-2">
              {selectedUser ? (
                <div className="space-y-6">
                  {/* Basic Info */}
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <span className="block text-sm text-gray-500">Name</span>
                        <p className="font-medium">{selectedUser.name}</p>
                      </div>
                      <div>
                        <span className="block text-sm text-gray-500">Age</span>
                        <p className="font-medium">{selectedUser.age} years</p>
                      </div>
                      <div>
                        <span className="block text-sm text-gray-500">Gender</span>
                        <p className="font-medium capitalize">{selectedUser.gender}</p>
                      </div>
                      <div>
                        <span className="block text-sm text-gray-500">Height</span>
                        <p className="font-medium">{selectedUser.height.replace('.', '\'')}"</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center space-x-2">
                        <Mail className="w-4 h-4 text-gray-400" />
                        <span className="text-sm">{selectedUser.email}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <span className="text-sm">{selectedUser.phone}</span>
                      </div>
                    </div>
                  </div>

                  {/* Professional Info */}
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Professional Information</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <span className="block text-sm text-gray-500">Education</span>
                        <p className="font-medium">{formatText(selectedUser.education)}</p>
                      </div>
                      <div>
                        <span className="block text-sm text-gray-500">Occupation</span>
                        <p className="font-medium">{formatText(selectedUser.occupation)}</p>
                      </div>
                      <div>
                        <span className="block text-sm text-gray-500">Annual Income</span>
                        <p className="font-medium">{selectedUser.annualIncome} LPA</p>
                      </div>
                      <div>
                        <span className="block text-sm text-gray-500">Work Location</span>
                        <p className="font-medium">
                          {selectedUser.workLocation?.type === 'nri' 
                            ? formatText(selectedUser.workLocation?.country || '')
                            : `${formatText(selectedUser.workLocation?.city || '')}, ${formatText(selectedUser.workLocation?.state || '')}`
                          }
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Personal Details */}
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Details</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <span className="block text-sm text-gray-500">Caste</span>
                        <p className="font-medium">{selectedUser.caste}</p>
                      </div>
                      <div>
                        <span className="block text-sm text-gray-500">Subcaste</span>
                        <p className="font-medium">{selectedUser.subcaste}</p>
                      </div>
                      <div>
                        <span className="block text-sm text-gray-500">Gothra</span>
                        <p className="font-medium">{selectedUser.gothra}</p>
                      </div>
                      <div>
                        <span className="block text-sm text-gray-500">Marital Status</span>
                        <p className="font-medium">{selectedUser.maritalStatus}</p>
                      </div>
                    </div>
                  </div>

                  {/* Family Location */}
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Family Location</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <span className="block text-sm text-gray-500">State</span>
                        <p className="font-medium">{formatText(selectedUser.familyLocation.state)}</p>
                      </div>
                      <div>
                        <span className="block text-sm text-gray-500">City</span>
                        <p className="font-medium">{formatText(selectedUser.familyLocation.city)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Documents */}
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Documents Verification</h3>
                    
                    <div className="space-y-4">
                      {/* Aadhaar */}
                      <div className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <FileText className="w-4 h-4 text-blue-600" />
                            <span className="font-medium">Aadhaar Card</span>
                          </div>
                          {selectedUser.aadhaarFile && (
                            <button className="text-blue-600 hover:text-blue-700 text-sm flex items-center space-x-1">
                              <Eye className="w-4 h-4" />
                              <span>View</span>
                            </button>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">
                          Number: {selectedUser.aadhaarNumber}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {selectedUser.aadhaarFile ? 'Document uploaded' : 'No document uploaded'}
                        </p>
                      </div>

                      {/* PAN */}
                      {selectedUser.panNumber && (
                        <div className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <CreditCard className="w-4 h-4 text-green-600" />
                              <span className="font-medium">PAN Card</span>
                            </div>
                            {selectedUser.panFile && (
                              <button className="text-blue-600 hover:text-blue-700 text-sm flex items-center space-x-1">
                                <Eye className="w-4 h-4" />
                                <span>View</span>
                              </button>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">
                            Number: {selectedUser.panNumber}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {selectedUser.panFile ? 'Document uploaded' : 'No document uploaded'}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Verification Status */}
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Verification Status</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <span className="block text-sm text-gray-500">Email Verified</span>
                        <p className="font-medium flex items-center space-x-1">
                          {selectedUser.emailVerified ? (
                            <><Check className="w-4 h-4 text-green-600" /><span>Yes</span></>
                          ) : (
                            <><X className="w-4 h-4 text-red-600" /><span>No</span></>
                          )}
                        </p>
                      </div>
                      <div>
                        <span className="block text-sm text-gray-500">Phone Verified</span>
                        <p className="font-medium flex items-center space-x-1">
                          {selectedUser.phoneVerified ? (
                            <><Check className="w-4 h-4 text-green-600" /><span>Yes</span></>
                          ) : (
                            <><X className="w-4 h-4 text-red-600" /><span>No</span></>
                          )}
                        </p>
                      </div>
                      <div>
                        <span className="block text-sm text-gray-500">Verification Score</span>
                        <p className="font-medium">{selectedUser.verificationScore}/100</p>
                      </div>
                    </div>
                  </div>

                  {/* Verification Actions */}
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Verification Decision</h3>
                    
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Notes (Optional)
                      </label>
                      <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={3}
                        className="w-full border border-gray-300 rounded-lg p-3 text-sm"
                        placeholder="Add any notes about this verification..."
                      />
                    </div>

                    <div className="flex space-x-4">
                      <button
                        onClick={() => handleVerifyUser('approve')}
                        disabled={verifying}
                        className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 transition-colors"
                      >
                        {verifying ? (
                          <LoadingSpinner size="sm" text="" />
                        ) : (
                          <>
                            <Check className="w-4 h-4" />
                            <span>Approve & Verify</span>
                          </>
                        )}
                      </button>
                      
                      <button
                        onClick={() => handleVerifyUser('reject')}
                        disabled={verifying}
                        className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 transition-colors"
                      >
                        {verifying ? (
                          <LoadingSpinner size="sm" text="" />
                        ) : (
                          <>
                            <X className="w-4 h-4" />
                            <span>Reject</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                  <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Select a User</h3>
                  <p className="text-gray-600">Choose a user from the list to review their verification details</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserVerification;